/**
 * TASK-231: Risk Profile Form Mapper
 *
 * Maps between ClientComplianceData and CompleteRiskProfile.
 */

import type { CompleteRiskProfile, PersonalInfo, FinancialSituation } from '../../../components/risk-profile/schemas/riskProfileSchema';
import type { ClientComplianceData, DataSource, TrackedField } from '../../../types/clientCompliance';
import { unwrap, createTrackedField, createDataSource } from '../../../types/clientCompliance';
import { transformDate, transformToDate } from '../transformers/dateTransformer';
import { formatAddressLine } from '../transformers/addressTransformer';
import type { MapperOptions } from '../types';

/**
 * Map ClientComplianceData to Risk Profile Section 1 (Personal Info)
 */
function mapToSection1(data: ClientComplianceData): Partial<PersonalInfo> {
  const personal = data.personal || {};
  const contact = data.contact || {};
  const employment = data.employment || {};
  // TASK-262: Use personalBackground for dependentsCount
  const personalBackground = data.personalBackground;

  const dob = unwrap(personal.dateOfBirth);
  const dateOfBirth = dob ? transformToDate(dob) : undefined;

  const address = unwrap(contact.residentialAddress);
  const addressLine = address ? formatAddressLine(address) : '';

  // TASK-244: Direct mapping - both use camelCase enums now
  // Filter to only values valid in Risk Profile schema
  const rawEmploymentStatus = unwrap(employment.employmentStatus);
  const validEmploymentStatuses = ['employed', 'selfEmployed', 'retired', 'unemployed', 'student'] as const;
  const employmentStatus = rawEmploymentStatus && validEmploymentStatuses.includes(rawEmploymentStatus as typeof validEmploymentStatuses[number])
    ? (rawEmploymentStatus as typeof validEmploymentStatuses[number])
    : 'employed';

  const rawMaritalStatus = unwrap(personal.maritalStatus);
  const validMaritalStatuses = ['single', 'married', 'divorced', 'widowed', 'separated'] as const;
  const maritalStatus = rawMaritalStatus && validMaritalStatuses.includes(rawMaritalStatus as typeof validMaritalStatuses[number])
    ? (rawMaritalStatus as typeof validMaritalStatuses[number])
    : 'single';

  // TASK-244: Updated to use camelCase flat structure
  return {
    firstName: unwrap(personal.firstName) || '',
    middleName: unwrap(personal.middleName),
    lastName: unwrap(personal.lastName) || '',
    dateOfBirth: dateOfBirth,
    residentialAddress: addressLine || undefined,
    phonePrimary: unwrap(contact.phonePrimary),
    emailPrimary: unwrap(contact.emailPrimary),
    nationalities: unwrap(personal.nationalities) || [],
    taxResidence: unwrap(data.tax?.taxResidence),
    employmentStatus: employmentStatus,
    occupation: unwrap(employment.occupation),
    employerName: unwrap(employment.employerName),
    maritalStatus: maritalStatus,
    // TASK-262: Use personalBackground.dependentsCount if available
    dependentsCount: personalBackground ? (unwrap(personalBackground.dependentsCount) || 0) : 0,
  };
}

/**
 * Map ClientComplianceData to Risk Profile Section 3 (Financial Situation)
 */
function mapToSection3(data: ClientComplianceData): Partial<FinancialSituation> {
  const financial = data.financial || {};
  // TASK-262: Use wealthSummary and assetBreakdown for better financial mapping
  const wealthSummary = data.wealthSummary;
  const assetBreakdown = data.assetBreakdown;

  // TASK-262: Determine best source for liquid assets and net worth
  const liquidAssets = unwrap(wealthSummary?.liquidNetWorth) || unwrap(financial.liquidAssets);
  const netWorth = unwrap(wealthSummary?.netWorth) || unwrap(financial.netWorth);
  const sourceOfWealth = unwrap(wealthSummary?.sourceOfWealth) || unwrap(financial.sourceOfWealth);
  const sourceOfFunds = unwrap(wealthSummary?.sourceOfFunds);

  // TASK-262: Map asset breakdown percentages to absolute values if netWorth available
  const cashPercentage = unwrap(assetBreakdown?.cash) || 0;
  const securitiesPercentage = unwrap(assetBreakdown?.securities) || 0;
  const realEstatePercentage = unwrap(assetBreakdown?.real_estate) || 0;
  const businessPercentage = unwrap(assetBreakdown?.business_equity) || 0;

  // TASK-244: Updated to use camelCase flat structure
  return {
    // Income (flattened)
    incomeEmploymentSalary: undefined,
    incomeSelfEmployment: undefined,
    incomeRental: undefined,
    incomePension: undefined,
    incomeInvestment: undefined,
    incomeOther: undefined,

    // Assets (flattened) - TASK-262: Use asset breakdown if available
    assetsCashDeposits: netWorth && cashPercentage > 0
      ? Math.round(netWorth * (cashPercentage / 100))
      : liquidAssets,
    assetsSecuritiesInvestments: netWorth && securitiesPercentage > 0
      ? Math.round(netWorth * (securitiesPercentage / 100))
      : undefined,
    assetsRealEstateMarketValue: netWorth && realEstatePercentage > 0
      ? Math.round(netWorth * (realEstatePercentage / 100))
      : undefined,
    assetsBusinessInterests: netWorth && businessPercentage > 0
      ? Math.round(netWorth * (businessPercentage / 100))
      : undefined,
    assetsRetirementAccounts: undefined,
    assetsOther: undefined,

    // Liabilities (flattened)
    liabilitiesMortgages: undefined,
    liabilitiesConsumerLoans: undefined,
    liabilitiesBusinessLoans: undefined,
    liabilitiesOtherDebts: undefined,

    // Expenses (flattened)
    expensesMonthlyLiving: 0,
    expensesAnnualExtraordinary: undefined,

    // Portfolio (flattened) - TASK-262: Use wealthSummary data
    portfolioAmountToManage: netWorth || unwrap(financial.totalAssets) || 0,
    portfolioPercentageOfNetWorth: undefined,
    portfolioSourceOfFunds: sourceOfFunds ? 'other' : 'savings',
    portfolioSourceOtherDescription: sourceOfWealth || sourceOfFunds,

    maxLossCapacity: undefined, // Not tracked directly
  };
}

/**
 * Map ClientComplianceData to CompleteRiskProfile
 *
 * @param data - Shared compliance data
 * @param options - Mapper options
 * @returns Partial Risk Profile data
 */
export function mapToRiskProfile(
  data: ClientComplianceData,
  _options?: MapperOptions
): Partial<CompleteRiskProfile> {
  return {
    section1: mapToSection1(data) as PersonalInfo,
    section3: mapToSection3(data) as FinancialSituation,
    // Other sections require user input and cannot be derived from compliance data
  };
}

/**
 * Map CompleteRiskProfile back to ClientComplianceData
 *
 * @param formData - Risk Profile form data
 * @param source - Data source for tracking
 * @returns Partial shared compliance data
 */
export function mapFromRiskProfile(
  formData: Partial<CompleteRiskProfile>,
  source: DataSource
): Partial<ClientComplianceData> {
  const section1 = formData.section1;
  const section3 = formData.section3;

  const createField = <T>(value: T | undefined): TrackedField<T> | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    return createTrackedField(value as T, source);
  };

  // TASK-244: Updated to use camelCase flat structure
  const firstName = section1?.firstName;
  const lastName = section1?.lastName;

  // Map employment status back
  let employmentStatus: 'employed' | 'selfEmployed' | 'unemployed' | 'retired' | 'student' | 'other' | undefined;

  switch (section1?.employmentStatus) {
    case 'employed':
      employmentStatus = 'employed';
      break;
    case 'selfEmployed':
      employmentStatus = 'selfEmployed';
      break;
    case 'retired':
      employmentStatus = 'retired';
      break;
    case 'unemployed':
    case 'student':
      employmentStatus = 'unemployed';
      break;
    default:
      employmentStatus = undefined;
  }

  // Map marital status back
  let maritalStatus: 'single' | 'married' | 'divorced' | 'widowed' | 'separated' | 'domestic_partnership' | undefined;

  switch (section1?.maritalStatus) {
    case 'single':
      maritalStatus = 'single';
      break;
    case 'married':
      maritalStatus = 'married';
      break;
    case 'divorced':
    case 'separated':
      maritalStatus = 'divorced';
      break;
    case 'widowed':
      maritalStatus = 'widowed';
      break;
    default:
      maritalStatus = undefined;
  }

  return {
    personal: {
      firstName: createField(firstName),
      middleName: createField(section1?.middleName),
      lastName: createField(lastName),
      dateOfBirth: createField(section1?.dateOfBirth ? transformDate(section1.dateOfBirth) : undefined),
      nationalities: section1?.nationalities ? createField(section1.nationalities) : undefined,
      maritalStatus: createField(maritalStatus),
    },
    contact: {
      phonePrimary: createField(section1?.phonePrimary),
      emailPrimary: createField(section1?.emailPrimary),
      // Address parsing from string would require more complex logic
    },
    employment: {
      employmentStatus: createField(employmentStatus),
      occupation: createField(section1?.occupation),
      employerName: createField(section1?.employerName),
    },
    financial: {
      totalAssets: createField(
        (section3?.assetsCashDeposits || 0) +
        (section3?.assetsSecuritiesInvestments || 0) +
        (section3?.assetsRealEstateMarketValue || 0) +
        (section3?.assetsBusinessInterests || 0) +
        (section3?.assetsRetirementAccounts || 0) +
        (section3?.assetsOther || 0)
      ),
      liquidAssets: createField(section3?.assetsCashDeposits),
      annualIncome: createField(
        (section3?.incomeEmploymentSalary || 0) +
        (section3?.incomeSelfEmployment || 0) +
        (section3?.incomeRental || 0) +
        (section3?.incomePension || 0) +
        (section3?.incomeInvestment || 0) +
        (section3?.incomeOther || 0)
      ),
      totalLiabilities: createField(
        (section3?.liabilitiesMortgages || 0) +
        (section3?.liabilitiesConsumerLoans || 0) +
        (section3?.liabilitiesBusinessLoans || 0) +
        (section3?.liabilitiesOtherDebts || 0)
      ),
      sourceOfWealth: createField(section3?.portfolioSourceOtherDescription),
    },
    tax: {
      taxResidence: createField(section1?.taxResidence),
    },
  };
}

/**
 * Create a Risk Profile data source
 */
export function createRiskProfileSource(clientId: string, userId?: string): DataSource {
  return createDataSource(
    'manual',
    `riskprofile_${clientId}_${Date.now()}`,
    'Risk Profile',
    1.0,
    undefined,
    userId
  );
}
